/* ===================== Gemstone consultation settings =====================
   Raju Maharaj's second consultation, managed from his own "Settings" tab
   (rendered under the pricing slabs in PricingSettingsTab). Reads/writes
   GET|PUT /astrologer/gemstone-consultation — the API answers 403 for any
   other login. The admin panel edits the same row; the latest save wins.

   Every day between start and end time is bookable in `duration` steps; the
   price is one fixed amount per session. */
import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@astro/api'

const DURATIONS = [15, 30, 45, 60]
const TYPES = [
  { value: 'video', label: 'Video call' },
  { value: 'phone', label: 'Phone call' },
  { value: 'inperson', label: 'In person' },
]
const QUERY_KEY = ['dashboard', 'gemstone-consultation']

const inputCls =
  'w-full bg-white border border-stone-200 rounded-xl px-4 py-3 text-sm font-semibold text-astroDark tabular-nums focus:outline-none focus:border-goldAccent/50 transition-colors'

function toForm(s) {
  return {
    is_enabled: Boolean(s?.is_enabled),
    rate: s?.rate != null ? String(Number(s.rate)) : '',
    duration: String(s?.duration ?? 30),
    start_time: s?.start_time ?? '10:00',
    end_time: s?.end_time ?? '20:00',
    consultation_types: s?.consultation_types ?? ['video', 'phone'],
  }
}

export default function GemstoneConsultationSettings() {
  const queryClient = useQueryClient()
  const [form, setForm] = useState(null)
  const [errors, setErrors] = useState({})
  const [banner, setBanner] = useState(null)
  const [toast, setToast] = useState(null)
  const toastTimer = useRef(null)
  useEffect(() => () => clearTimeout(toastTimer.current), [])

  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => api.bookings.gemstoneSettings(),
    retry: false,
    select: (res) => res?.data ?? null,
  })

  useEffect(() => {
    if (query.data) setForm(toForm(query.data))
  }, [query.data])

  const saveMutation = useMutation({
    mutationFn: (payload) => api.bookings.updateGemstoneSettings(payload),
    onSuccess: () => {
      setErrors({})
      setBanner(null)
      clearTimeout(toastTimer.current)
      setToast('Gemstone consultation updated.')
      toastTimer.current = setTimeout(() => setToast(null), 3500)
      queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    },
    onError: (err) => {
      const details = err?.details && typeof err.details === 'object' ? err.details : {}
      setErrors(Object.fromEntries(Object.entries(details).map(([k, v]) => [k, Array.isArray(v) ? v[0] : String(v)])))
      setBanner(err?.message || 'Could not save the gemstone consultation. Please try again.')
    },
  })

  // 403 = not Raju Maharaj's login; the section simply doesn't apply.
  if (query.isError) return null
  if (!form) return <div className="dash-card p-4 sm:p-6 h-40 animate-pulse" aria-busy="true" />

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }))
  const toggleType = (t) =>
    set('consultation_types', form.consultation_types.includes(t) ? form.consultation_types.filter((x) => x !== t) : [...form.consultation_types, t])

  const onSave = (e) => {
    e.preventDefault()
    setBanner(null)
    saveMutation.mutate({
      is_enabled: form.is_enabled,
      rate: Number(form.rate || 0),
      duration: Number(form.duration),
      start_time: form.start_time,
      end_time: form.end_time,
      consultation_types: form.consultation_types,
    })
  }

  return (
    <form onSubmit={onSave} className="space-y-6">
      <div className="dash-card p-4 sm:p-6">
        <div className="flex items-center justify-between gap-3 mb-1">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-xl bg-[#F5E6D3] flex items-center justify-center text-base shrink-0">💎</span>
            <div>
              <h3 className="text-lg text-astroDark font-royal leading-tight">Gemstone Consultation</h3>
              <p className="text-[11px] text-stone-500">Bookable every day inside these hours, at one fixed price per session.</p>
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={form.is_enabled}
            aria-label="Gemstone consultation open for booking"
            onClick={() => set('is_enabled', !form.is_enabled)}
            className={`relative w-12 h-7 rounded-full transition-colors shrink-0 ${form.is_enabled ? 'bg-[#A67C00]' : 'bg-stone-300'}`}
          >
            <span className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-all ${form.is_enabled ? 'left-6' : 'left-1'}`} />
          </button>
        </div>
        <p className="text-[11px] text-stone-500 mt-1">
          {form.is_enabled ? 'Open — customers can book it now.' : 'Closed — customers won’t see it until you switch it on.'}
        </p>
        {errors.is_enabled && <p className="text-[10px] text-red-500 mt-1.5">{errors.is_enabled}</p>}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A67C00]">Price per session</span>
            <div className="relative mt-2">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-stone-500 font-semibold pointer-events-none">₹</span>
              <input type="number" min={0} placeholder="1500" value={form.rate} onChange={(e) => set('rate', e.target.value)} className={`${inputCls} pl-8`} />
            </div>
            {errors.rate && <p className="text-[10px] text-red-500 mt-1.5">{errors.rate}</p>}
          </label>
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A67C00]">Session length</span>
            <select value={form.duration} onChange={(e) => set('duration', e.target.value)} className={`${inputCls} mt-2`}>
              {DURATIONS.map((d) => (
                <option key={d} value={d}>{d} minutes</option>
              ))}
            </select>
            {errors.duration && <p className="text-[10px] text-red-500 mt-1.5">{errors.duration}</p>}
          </label>
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A67C00]">Daily start time</span>
            <input type="time" value={form.start_time} onChange={(e) => set('start_time', e.target.value)} className={`${inputCls} mt-2`} />
            {errors.start_time && <p className="text-[10px] text-red-500 mt-1.5">{errors.start_time}</p>}
          </label>
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A67C00]">Daily end time</span>
            <input type="time" value={form.end_time} onChange={(e) => set('end_time', e.target.value)} className={`${inputCls} mt-2`} />
            {errors.end_time && <p className="text-[10px] text-red-500 mt-1.5">{errors.end_time}</p>}
          </label>
        </div>

        <div className="mt-5">
          <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A67C00] mb-2">Offered as</p>
          <div className="flex flex-wrap gap-2">
            {TYPES.map((t) => {
              const on = form.consultation_types.includes(t.value)
              return (
                <button
                  key={t.value}
                  type="button"
                  aria-pressed={on}
                  onClick={() => toggleType(t.value)}
                  className={`px-4 py-2 rounded-xl text-xs font-semibold border transition-colors ${
                    on ? 'bg-[#A67C00] border-[#A67C00] text-white' : 'border-stone-200 text-stone-600 hover:border-goldAccent/50'
                  }`}
                >
                  {t.label}
                </button>
              )
            })}
          </div>
          {errors.consultation_types && <p className="text-[10px] text-red-500 mt-1.5">{errors.consultation_types}</p>}
        </div>
      </div>

      <div className="dash-card p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <p className={`text-xs ${banner ? 'font-semibold text-red-500' : 'text-stone-500'}`}>
          {banner ?? 'Times already booked (either consultation) are hidden from customers, so you’re never double-booked.'}
        </p>
        <button
          type="submit"
          disabled={saveMutation.isPending}
          className="shine-btn bg-gradient-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[11px] uppercase tracking-[0.2em] px-6 py-3 rounded-xl shadow-md shadow-goldPrimary/10 transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0 shrink-0"
        >
          {saveMutation.isPending ? 'Saving…' : 'Save Gemstone Settings'}
        </button>
      </div>

      {toast && (
        <div role="status" aria-live="polite" className="dash-toast flex items-center gap-2.5 bg-[#3D0A0A] text-white text-sm font-semibold pl-4 pr-5 py-3 rounded-xl shadow-2xl shadow-black/30">
          {toast}
        </div>
      )}
    </form>
  )
}
